import { History } from 'history'
import { cast, flow, getEnv, Instance, types } from 'mobx-state-tree'
import { CalculatorOptions } from '../calculator/calculator'
import { RouteDefinition, Routes, RoutesInOrder } from '../calculator/Routes'
import { Offer } from '../models/Offer'
import { Office, OfficeInstance } from '../models/Office'
import { Recommendation } from '../models/Recommendation'
import { IAPIService } from '../shared/APIService'
import { Jaw } from '../shared/Constants'
import { Utils } from '../shared/Utils'

interface StoreEnv {
  history: History
  api: IAPIService
  options: CalculatorOptions
}

export interface IStepInfo {
  title: string
  pathMatch: RegExp
  path?: string
  isDone?: () => boolean
}

export const CalculatorStore = types
  .model('CalculatorStore', {
    offer: types.maybeNull(Offer),
    offices: types.array(Office),
    videoVisible: types.optional(types.boolean, false),

    // Calculator state
    requirementsDone: types.optional(types.boolean, false),
    boneGraftingDone: types.optional(types.boolean, false),
    personalDataDone: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get steps(): { [key: string]: IStepInfo } {
      return {
        requirementsUpperJaw: {
          title: 'Bedarfsermittlung Oberkiefer',
          pathMatch: /^\/implantatkostenrechner\/oberkiefer/i,
          path: '/implantatkostenrechner/oberkiefer',
          isDone: () => {
            return self.requirementsDone
          },
        },
        requirementsLowerJaw: {
          title: 'Bedarfsermittlung Unterkiefer',
          pathMatch: /^\/implantatkostenrechner\/unterkiefer/i,
          path: '/implantatkostenrechner/unterkiefer',
          isDone: () => {
            return self.requirementsDone
          },
        },
        personalData: {
          title: 'Persönliche Daten',
          pathMatch: /^\/implantatkostenrechner\/(persoenliche-daten|versicherung)/i,
          path: '/implantatkostenrechner/persoenliche-daten',
          isDone: () => {
            return self.personalDataDone
          },
        },
        result: {
          title: 'Ergebnis',
          pathMatch: /^\/implantatkostenrechner\/ergebnis/i,
        },
      }
    },
  }))
  .actions((self) => {
    const actions = {
      setPersonalDataDone(personalDataDone: boolean) {
        self.personalDataDone = personalDataDone
      },

      goBack() {
        const routeIndex = RoutesInOrder.map((r) => r.path).indexOf(location.pathname)
        for (let i = routeIndex - 1; i >= 0; i--) {
          const newRoute = RoutesInOrder[i]
          if (!newRoute.goBackCondition || newRoute.goBackCondition(self)) {
            actions.redirectToRoute(newRoute)
            break
          }
        }
      },

      goForward() {
        const routeIndex = RoutesInOrder.map((r) => r.path).indexOf(location.pathname)
        for (let i = routeIndex + 1; i < RoutesInOrder.length; i++) {
          const newRoute = RoutesInOrder[i]
          if (!newRoute.goBackCondition || newRoute.goBackCondition(self)) {
            actions.redirectToRoute(newRoute)
            break
          }
        }
      },

      validateRequirementsGiven: function () {
        const { offer } = self
        if (!offer.upperJawRequirement) {
          actions.redirectToRoute(Routes.calculatorUpperJaw as RouteDefinition)
          return false
        } else if (!offer.lowerJawRequirement) {
          actions.redirectToRoute(Routes.calculatorLowerJaw as RouteDefinition)
          return false
        }
        return true
      },

      toggleVideo: function () {
        self.videoVisible = !self.videoVisible
      },

      // -------------------------------------------------------------------------- API

      fetchRecommendations: flow(function* () {
        const env = getEnv<StoreEnv>(self)
        const data = yield env.api.fetchRecommendations(self.offer, env.options.apiKey)
        if (data.upper_jaw_recommendations) {
          self.offer.setRecommendationsForJaw(
            'upper',
            data.upper_jaw_recommendations.map((recommendationData) => {
              if (recommendationData.attachment_type === 'locator') {
                const requirement = self.offer.getJawRequirement('upper')
                requirement.setMaterial('normal')
              }
              return Recommendation.create({
                attachmentType: recommendationData.attachment_type,
                count: recommendationData.count,
                jawType: recommendationData.jaw_type,
                recommendationType: recommendationData.recommendation_type,
              })
            })
          )
        }
        if (data.lower_jaw_recommendations) {
          self.offer.setRecommendationsForJaw(
            'lower',
            data.lower_jaw_recommendations.map((recommendationData) => {
              if (recommendationData.attachment_type === 'locator') {
                const requirement = self.offer.getJawRequirement('lower')
                requirement.setMaterial('normal')
              }

              return Recommendation.create({
                attachmentType: recommendationData.attachment_type,
                count: recommendationData.count,
                jawType: recommendationData.jaw_type,
                recommendationType: recommendationData.recommendation_type,
              })
            })
          )
        }
      }),

      fetchOffices: flow(function* (
        offerId?: string,
        limit: number = 3,
        options: { radius?: number; city_or_zip_code?: string; exclude_ids?: string[] } = {}
      ): OfficeInstance[] {
        options = Object.assign({}, options, {
          page: options.exclude_ids ? 1 : Math.floor(self.offices.length / limit) + 1,
          limit,
        })
        const data = yield getEnv<StoreEnv>(self).api.fetchOffices(offerId, options)
        const newOffices = data.map((officeData) =>
          Office.create({
            id: officeData.id,
            address: officeData.address,
            city: officeData.city,
            locationLatitude: officeData.location_latitude,
            locationLongitude: officeData.location_longitude,
            name: officeData.name,
            zipCode: officeData.zip_code,
            image: officeData.image,
            phone: officeData.phone,
            website: officeData.website,
            landingpage: officeData.landingpage,
            drFlexId: officeData.dr_flex_id,
            description: officeData.description,
            distance: officeData.distance,
            supportsAllOn4: officeData.supports_all_on_4,
            bookingUrl: officeData.booking_url,
            videoUrl: officeData.video_url,
          })
        )
        self.offices = cast(self.offices.concat(newOffices))
        return newOffices
      }),

      calculateOffer: flow(function* () {
        const env = getEnv<StoreEnv>(self)
        const data = yield env.api.calculateOffer(self.offer, env.options.apiKey, env.options.multiplier)
        self.offer = Offer.create(Utils.convertKeysFromUnderscoreToCamelCase(data))
      }),

      fetchOffer: flow(function* (offerId: string) {
        const env = getEnv<StoreEnv>(self)
        const data = yield env.api.fetchOffer(offerId)
        self.offer = Offer.create(Utils.convertKeysFromUnderscoreToCamelCase(data))
      }),

      clearOffices: () => (self.offices = cast([])),

      // -------------------------------------------------------------------------- REDIRECTS
      redirectToRoute: (route: RouteDefinition) => {
        getEnv<StoreEnv>(self).history.push(route.path)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },

      redirectToJawRequirements: (jaw: Jaw) => {
        switch (jaw) {
          case 'upper':
            actions.redirectToRoute(Routes.calculatorUpperJaw.subRoutes.requirements)
            break
          case 'lower':
            actions.redirectToRoute(Routes.calculatorLowerJaw.subRoutes.requirements)
            break
        }
      },
    }
    return actions
  })

export type CalculatorStoreInstance = Instance<typeof CalculatorStore>
